/**
 * ScoreHeader.tsx — Composite M-PACT Score Header Bar
 *
 * Fixed bar pinned to top of the helix viewport.
 * Renders: composite score number · forecast category · deal name
 *
 * Color + threshold authority: CANON.md §5 (5-band palette, locked 2026-05-23).
 * Forecast labels: Commit / Upside / Best Case / Pipeline / Omit.
 *
 * Brand:
 *   Black #0B0B0F (never "navy")
 *   Red #DC2626 (brand accent — separate from score-driven palette)
 *   Soft white #FAFAF7
 *   DM Sans for score number/label
 *   Inter for deal name
 */

import { useEffect, useRef, useState } from "react";
import { getHealthColor, getForecastCategory } from "@shared/schema";

interface ScoreBand {
  label: string;
  color: string;
}

function getScoreBand(score: number): ScoreBand {
  return { label: getForecastCategory(score), color: getHealthColor(score) };
}

// ─── Count-up animation hook ───────────────────────────────────────────────────

function useCountUp(target: number, durationMs = 800): number {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  const startRef = useRef(target);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const start = prevRef.current;
    const end = target;
    if (start === end) return;

    startRef.current = start;
    const t0 = performance.now();

    function step(now: number) {
      const elapsed = now - t0;
      const k = Math.min(1, elapsed / durationMs);
      // Linear ease per spec
      const val = Math.round(start + (end - start) * k);
      setDisplay(val);
      if (k < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        prevRef.current = end;
      }
    }

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, durationMs]);

  return display;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  /** 0–100 composite M-PACT score */
  score: number;
  /** Deal / account name displayed right-aligned */
  dealName?: string;
  /** Whether to render as a fixed overlay (true) or a normal block element */
  fixed?: boolean;
  /**
   * F-10: When true, suppress the "Disqualify risk" band even if score is 0.
   * Used when an account has no answers logged yet — a 0 score is not a
   * disqualification signal, it is an unscored state.
   */
  noData?: boolean;
  // TICKET-4: evidence cap display
  /**
   * When set, a cap notice is shown below the score bar.
   * capCount = number of answers without evidence.
   */
  evidenceCapCount?: number;
}

export default function ScoreHeader({ score, dealName, fixed = false, noData = false, evidenceCapCount }: Props) {
  const displayScore = useCountUp(score, 800);
  const band = noData && score === 0
    ? { label: "Not yet scored", color: "#94A3B8" }
    : getScoreBand(displayScore);
  // Track previous band for CSS color transition
  const prevBand = noData && score === 0
    ? { label: "Not yet scored", color: "#94A3B8" }
    : getScoreBand(score);

  return (
    <>
      {/* CSS color transition + font imports */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;700&family=Inter:wght@400;600&display=swap');

        .score-header {
          height: 48px;
          background: rgba(15, 23, 42, 0.92);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          padding: 0 20px;
          gap: 0;
          z-index: 100;
          transition: background-color 600ms ease;
          user-select: none;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .score-header--fixed {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
        }
        .score-header__eyebrow {
          font-family: 'Inter', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(107,114,128, 0.6);
          margin-right: 14px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .score-header__score {
          font-family: 'DM Sans', 'Inter', sans-serif;
          font-size: 28px;
          font-weight: 700;
          line-height: 1;
          font-variant-numeric: tabular-nums;
          transition: color 600ms ease;
          flex-shrink: 0;
        }
        .score-header__dot {
          font-family: 'DM Sans', 'Inter', sans-serif;
          font-size: 20px;
          font-weight: 400;
          color: rgba(107,114,128, 0.5);
          margin: 0 8px;
          flex-shrink: 0;
        }
        .score-header__label {
          font-family: 'DM Sans', 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          line-height: 1;
          transition: color 600ms ease;
          flex-shrink: 0;
        }
        .score-header__deal {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 400;
          color: rgba(250, 250, 247, 0.6);
          margin-left: auto;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 260px;
        }
        /* Mobile tightening: shrink paddings + label so the deal name fits */
        @media (max-width: 480px) {
          .score-header { padding: 0 10px; }
          .score-header__eyebrow { margin-right: 8px; font-size: 8px; }
          .score-header__score { font-size: 22px; }
          .score-header__dot { margin: 0 5px; font-size: 16px; }
          .score-header__label { font-size: 12px; }
          .score-header__deal { font-size: 11px; max-width: 130px; }
        }
      `}</style>

      <div
        className={`score-header${fixed ? " score-header--fixed" : ""}`}
        role="status"
        aria-label={`Composite score: ${displayScore}, ${band.label}`}
      >
        <span className="score-header__eyebrow">Composite</span>
        <span
          className="score-header__score"
          style={{ color: band.color }}
        >
          {displayScore}
        </span>
        <span className="score-header__dot">·</span>
        <span
          className="score-header__label"
          style={{ color: band.color + "CC" }} // 80% opacity via hex CC
        >
          {band.label}
        </span>
        {dealName && (
          <span className="score-header__deal">{dealName}</span>
        )}
      </div>

      {/* TICKET-4: evidence cap notice */}
      {evidenceCapCount !== undefined && evidenceCapCount > 0 && (
        <div
          role="alert"
          style={{
            background: "rgba(220,38,38,0.08)",
            borderBottom: "1px solid rgba(220,38,38,0.15)",
            padding: "6px 20px",
            fontSize: 12,
            fontFamily: "'Inter', sans-serif",
            color: "#DC2626",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#DC2626",
              flexShrink: 0,
            }}
          />
          Score is capped because {evidenceCapCount} answer{evidenceCapCount === 1 ? "" : "s"} lack evidence. Add evidence to unlock a higher score.
        </div>
      )}
    </>
  );
}
